import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr } from "@/lib/utils-format";
import { FileSpreadsheet, Download, Calculator, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function MonthPicker({ year, month, onChange }) {
  const now = new Date();
  return (
    <div className="flex items-end gap-2">
      <div>
        <Label>Month</Label>
        <Select value={String(month)} onValueChange={(v) => onChange(year, Number(v))}>
          <SelectTrigger className="w-32" data-testid="gst-month"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Year</Label>
        <Select value={String(year)} onValueChange={(v) => onChange(Number(v), month)}>
          <SelectTrigger className="w-24" data-testid="gst-year"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

const downloadFile = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = filename;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
  } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
};

// ==============================================================
// TAX SUMMARY (main dashboard)
// ==============================================================
export function GstTaxSummaryPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gst/tax-summary", { params: { year, month } });
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  return (
    <div className="space-y-6" data-testid="gst-tax-summary">
      <div className="flex items-end flex-wrap gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="ml-auto flex gap-2">
          <Button onClick={() => downloadFile(`/gst/export/gstr1?year=${year}&month=${month}`, `GSTR-1_${year}-${String(month).padStart(2, "0")}.xlsx`)}
                  data-testid="gst-export-gstr1"
                  className="bg-[#C05621] hover:bg-[#9C4221] text-white">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Export GSTR-1
          </Button>
          <Button onClick={() => downloadFile(`/gst/export/gstr3b?year=${year}&month=${month}`, `GSTR-3B_${year}-${String(month).padStart(2, "0")}.xlsx`)}
                  data-testid="gst-export-gstr3b"
                  variant="outline" className="border-[#C05621] text-[#C05621] hover:bg-[#FEEBC8]">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Export GSTR-3B
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-[#8B7355]">Loading…</div>}

      {d && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="gst-output">
              <div className="flex items-center gap-2 uc-label"><ArrowUpRight className="w-3 h-3 text-emerald-600" /> Output GST</div>
              <div className="font-[Outfit] text-3xl font-semibold mt-1 text-emerald-700">{inr(d.output_gst.total)}</div>
              <div className="text-xs text-[#8B7355] mt-2 space-y-0.5">
                <div>CGST <b className="text-slate-800">{inr(d.output_gst.cgst)}</b></div>
                <div>SGST <b className="text-slate-800">{inr(d.output_gst.sgst)}</b></div>
                <div>IGST <b className="text-slate-800">{inr(d.output_gst.igst)}</b></div>
                <div className="text-[10px] mt-1">from {d.output_gst.invoice_count} invoice{d.output_gst.invoice_count === 1 ? "" : "s"} · taxable {inr(d.output_gst.taxable_value)}</div>
              </div>
            </div>
            <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="gst-input">
              <div className="flex items-center gap-2 uc-label"><ArrowDownLeft className="w-3 h-3 text-slate-500" /> Input GST</div>
              <div className="font-[Outfit] text-3xl font-semibold mt-1 text-slate-800">{inr(d.input_gst.total)}</div>
              <div className="text-xs text-[#8B7355] mt-2 space-y-0.5">
                <div>CGST <b className="text-slate-800">{inr(d.input_gst.cgst)}</b></div>
                <div>SGST <b className="text-slate-800">{inr(d.input_gst.sgst)}</b></div>
                <div>IGST <b className="text-slate-800">{inr(d.input_gst.igst)}</b></div>
                <div className="text-[10px] mt-1">from {d.input_gst.bill_count} purchase bill{d.input_gst.bill_count === 1 ? "" : "s"} · taxable {inr(d.input_gst.taxable_value)}</div>
              </div>
            </div>
            <div className="bg-[#FEEBC8] border-2 border-[#C05621] rounded-lg p-5" data-testid="gst-payable">
              <div className="flex items-center gap-2 uc-label text-[#7B341E]"><Calculator className="w-3 h-3" /> GST Payable</div>
              <div className={`font-[Outfit] text-3xl font-semibold mt-1 ${d.payable.total >= 0 ? "text-[#7B341E]" : "text-emerald-700"}`}>
                {inr(d.payable.total)}
              </div>
              <div className="text-xs text-[#7B341E] mt-2 space-y-0.5">
                <div>CGST <b>{inr(d.payable.cgst)}</b></div>
                <div>SGST <b>{inr(d.payable.sgst)}</b></div>
                <div>IGST <b>{inr(d.payable.igst)}</b></div>
                <div className="text-[10px] mt-1 opacity-80">Output − Input = Payable to government</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#EAE5D9] rounded-lg p-4 flex items-center gap-3">
            <Calculator className="w-5 h-5 text-[#C05621]" />
            <div className="text-sm text-[#5C4033] font-mono">
              <b>{inr(d.output_gst.total)}</b> (Output) − <b>{inr(d.input_gst.total)}</b> (Input) = <b className="text-[#7B341E]">{inr(d.payable.total)}</b> payable for {MONTHS[month - 1]} {year}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ==============================================================
// SALES / PURCHASE REGISTERS
// ==============================================================
function RegisterPanel({ kind }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gst/${kind}-register`, { params: { year, month } });
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const t = d?.totals || {};
  return (
    <div className="space-y-4" data-testid={`gst-${kind}-register`}>
      <div className="flex items-end flex-wrap gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="ml-auto text-xs text-[#8B7355]">
          {loading ? "Loading…" : `${d?.count ?? 0} rows · taxable ${inr(t.taxable_value)} · GST ${inr(t.total_tax)}`}
        </div>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">{kind === "sales" ? "Invoice" : "Bill"}</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">{kind === "sales" ? "Customer" : "Supplier"}</th>
              <th className="px-4 py-3">GSTIN</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Taxable</th>
              <th className="px-4 py-3 text-right">CGST</th>
              <th className="px-4 py-3 text-right">SGST</th>
              <th className="px-4 py-3 text-right">IGST</th>
              <th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody data-testid={`gst-${kind}-rows`}>
            {(d?.rows || []).map((r) => (
              <tr key={r.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`gst-${kind}-row-${r.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{r.invoice_number || r.bill_number}</td>
                <td className="px-4 py-2 text-xs text-[#8B7355]">{r.invoice_date || r.bill_date}</td>
                <td className="px-4 py-2">{r.customer_name || r.supplier_name}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.customer_gstin || r.supplier_gstin || "—"}</td>
                <td className="px-4 py-2">
                  {r.invoice_type ? <Badge variant="outline" className="text-[10px]">{r.invoice_type}</Badge> : null}
                  {r.is_interstate ? <Badge className="bg-blue-600 text-white ml-1 text-[10px]">IGST</Badge> : null}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.taxable_value)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.cgst ? inr(r.cgst) : "—"}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.sgst ? inr(r.sgst) : "—"}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.igst ? inr(r.igst) : "—"}</td>
                <td className="px-4 py-2 text-right font-semibold">{inr(r.grand_total)}</td>
              </tr>
            ))}
            {(d?.rows || []).length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#8B7355]">{loading ? "Loading…" : "No entries for this month"}</td></tr>
            )}
            {(d?.rows || []).length > 0 && (
              <tr className="bg-[#FAF6EE] border-t-2 border-[#2C1810] font-semibold">
                <td colSpan={5} className="px-4 py-2 text-right uc-label">Totals</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.taxable_value)}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.cgst)}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.sgst)}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.igst)}</td>
                <td className="px-4 py-2 text-right">{inr(t.grand_total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const SalesRegisterPanel = () => <RegisterPanel kind="sales" />;
export const PurchaseRegisterPanel = () => <RegisterPanel kind="purchase" />;

// ==============================================================
// HSN SUMMARY
// ==============================================================
export function HsnSummaryPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gst/hsn-summary", { params: { year, month } });
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const t = d?.totals || {};
  return (
    <div className="space-y-4" data-testid="gst-hsn-summary">
      <div className="flex items-end flex-wrap gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="ml-auto text-xs text-[#8B7355]">
          {loading ? "Loading…" : `${t.hsn_codes ?? 0} HSN codes · taxable ${inr(t.taxable_value)} · GST ${inr(t.total_tax)}`}
        </div>
      </div>
      <div className="bg-[#FEEBC8] border border-[#F6AD55] rounded-md p-3 text-xs text-[#7B341E]">
        This is Table 12 of GSTR-1. Bakery products default to <b>HSN 1905</b> if not explicitly set on the product. Update the product's HSN code from Products → Edit to override.
      </div>
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">HSN Code</th>
              <th className="px-4 py-3">Unit</th>
              <th className="px-4 py-3 text-right">GST Rate</th>
              <th className="px-4 py-3 text-right">Total Qty</th>
              <th className="px-4 py-3 text-right">Taxable Value</th>
              <th className="px-4 py-3 text-right">CGST</th>
              <th className="px-4 py-3 text-right">SGST</th>
              <th className="px-4 py-3 text-right">IGST</th>
              <th className="px-4 py-3 text-right">Total Tax</th>
            </tr>
          </thead>
          <tbody data-testid="gst-hsn-rows">
            {(d?.rows || []).map((r, i) => (
              <tr key={i} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`gst-hsn-row-${r.hsn_code}-${r.gst_rate}`}>
                <td className="px-4 py-2 font-mono">{r.hsn_code}</td>
                <td className="px-4 py-2 text-xs">{r.unit}</td>
                <td className="px-4 py-2 text-right"><Badge variant="outline">{r.gst_rate}%</Badge></td>
                <td className="px-4 py-2 text-right font-mono">{r.quantity}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(r.taxable_value)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.cgst ? inr(r.cgst) : "—"}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.sgst ? inr(r.sgst) : "—"}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.igst ? inr(r.igst) : "—"}</td>
                <td className="px-4 py-2 text-right font-semibold">{inr(r.total_tax)}</td>
              </tr>
            ))}
            {(d?.rows || []).length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#8B7355]">{loading ? "Loading…" : "No invoices for this month"}</td></tr>
            )}
            {(d?.rows || []).length > 0 && (
              <tr className="bg-[#FAF6EE] border-t-2 border-[#2C1810] font-semibold">
                <td colSpan={4} className="px-4 py-2 text-right uc-label">Totals</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.taxable_value)}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.cgst)}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.sgst)}</td>
                <td className="px-4 py-2 text-right font-mono">{inr(t.igst)}</td>
                <td className="px-4 py-2 text-right">{inr(t.total_tax)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
